import { type ColumnDef, createColumnHelper } from "@tanstack/react-table"

export interface TransactionRow {
  id: string
  inserted_at: string
  student_name: string
  phone: string
  video_title: string
  amount_kes: number
  provider: string
  status: "success" | "pending" | "failed" | "refunded"
  receipt_number: string | null
  receipt_url: string | null
}

const col = createColumnHelper<TransactionRow>()

const statusConfig = {
  success: { dot: "bg-emerald-500", badge: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  pending: { dot: "bg-amber-500", badge: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  failed: { dot: "bg-red-500", badge: "bg-red-500/10 text-red-500 border-red-500/20" },
  refunded: { dot: "bg-muted-foreground", badge: "bg-secondary text-muted-foreground border-border" },
} as const

export const transactionColumns: ColumnDef<TransactionRow, any>[] = [
  col.accessor("inserted_at", {
    header: "When",
    cell: (info) => (
      <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor("student_name", {
    header: "Student",
    cell: (info) => {
      const row = info.row.original
      const initials = row.student_name
        .split(" ")
        .slice(0, 2)
        .map((n: string) => n[0] ?? "")
        .join("")
        .toUpperCase()
      return (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="size-7 rounded-md bg-secondary border border-border flex items-center justify-center shrink-0">
            <span className="text-[0.6rem] font-semibold text-foreground">{initials}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[0.8125rem] font-medium text-foreground truncate leading-tight">
              {row.student_name}
            </p>
            <p className="text-[0.65rem] text-muted-foreground font-mono tabular-nums leading-tight">
              {row.phone}
            </p>
          </div>
        </div>
      )
    },
  }),
  col.accessor("video_title", {
    header: "Video",
    cell: (info) => (
      <p className="text-xs text-foreground truncate max-w-[16rem]">
        {info.getValue() || "—"}
      </p>
    ),
  }),
  col.accessor("amount_kes", {
    header: "Amount",
    cell: (info) => (
      <span className="text-xs font-semibold text-foreground tabular-nums">
        KES {info.getValue()}
      </span>
    ),
    meta: { align: "right" },
  }),
  col.accessor("provider", {
    header: "Provider",
    cell: (info) => (
      <span className="inline-flex items-center text-[0.7rem] font-medium px-2 py-0.5 rounded-md border bg-secondary text-foreground border-border capitalize">
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor("status", {
    header: "Status",
    cell: (info) => {
      const s = info.getValue() as keyof typeof statusConfig
      const cfg = statusConfig[s] ?? statusConfig.refunded
      return (
        <span className={`inline-flex items-center gap-1.5 text-[0.7rem] font-medium px-2 py-0.5 rounded-md border capitalize ${cfg.badge}`}>
          <span className={`size-1.5 rounded-full ${cfg.dot}`} />
          {s}
        </span>
      )
    },
  }),
  col.accessor("receipt_number", {
    header: "Receipt",
    cell: (info) => {
      const row = info.row.original
      if (row.status === "success" && row.receipt_url) {
        return (
          <a href={row.receipt_url} className="text-[0.68rem] font-mono tabular-nums text-primary hover:underline">
            {info.getValue() || "—"}
          </a>
        )
      }
      return <span className="text-[0.68rem] font-mono tabular-nums text-muted-foreground">{info.getValue() || "—"}</span>
    },
  }),
]

// Simpler variant for the dashboard widget (no receipt column)
export const dashboardTransactionColumns: ColumnDef<TransactionRow, any>[] = [
  col.accessor("student_name", {
    header: "Student",
    cell: (info) => {
      const row = info.row.original
      const initials = row.student_name
        .split(" ")
        .slice(0, 2)
        .map((n: string) => n[0] ?? "")
        .join("")
        .toUpperCase()
      return (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="size-7 rounded-md bg-secondary border border-border flex items-center justify-center shrink-0">
            <span className="text-[0.6rem] font-semibold text-foreground">{initials}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[0.8125rem] font-medium text-foreground truncate leading-tight max-w-[120px]">
              {row.student_name}
            </p>
            <p className="text-[0.65rem] text-muted-foreground font-mono tabular-nums leading-tight">
              {row.phone}
            </p>
          </div>
        </div>
      )
    },
  }),
  col.accessor("video_title", {
    header: "Video",
    cell: (info) => (
      <p className="text-xs text-foreground truncate max-w-[14rem]">{info.getValue() || "—"}</p>
    ),
  }),
  col.accessor("amount_kes", {
    header: "Amount",
    cell: (info) => (
      <span className="text-xs font-semibold text-foreground tabular-nums">KES {info.getValue()}</span>
    ),
  }),
  col.accessor("status", {
    header: "Status",
    cell: (info) => {
      const s = info.getValue() as keyof typeof statusConfig
      const cfg = statusConfig[s] ?? statusConfig.refunded
      return (
        <span className={`inline-flex items-center gap-1.5 text-[0.7rem] font-medium px-2 py-0.5 rounded-md border capitalize ${cfg.badge}`}>
          <span className={`size-1.5 rounded-full ${cfg.dot}`} />
          {s}
        </span>
      )
    },
  }),
  col.accessor("provider", {
    header: "Provider",
    cell: (info) => (
      <span className="inline-flex items-center text-[0.7rem] font-medium px-2 py-0.5 rounded-md border bg-secondary text-foreground border-border capitalize">
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor("inserted_at", {
    header: "When",
    cell: (info) => (
      <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{info.getValue()}</span>
    ),
  }),
]
